import React, { useEffect, useState } from 'react';
import { useVehicles } from '../../hooks/useVehicles';
import { useNavigate } from 'react-router-dom';
import StatCard from './StatCard';
import RecentTrips from './RecentTrips';
import tripService from '../../services/tripService';
import Loader from '../common/Loader';
import { formatCurrency, formatDate } from '../../utils/formatters';

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const { vehicles } = useVehicles();
  const [summary, setSummary] = useState(null);
  const [topVehicle, setTopVehicle] = useState(null);
  const [topDriver, setTopDriver] = useState(null);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [dueSoonCount, setDueSoonCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const dateOnly = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const dueState = (value) => {
    const dueDate = dateOnly(value);
    if (!dueDate) return null;
    const today = dateOnly(new Date());
    const in30Days = new Date(today);
    in30Days.setDate(in30Days.getDate() + 30);
    if (dueDate < today) return 'overdue';
    if (dueDate <= in30Days) return 'due_soon';
    return 'ok';
  };

  const asNumber = (value) => Number(value || 0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const monthStart = new Date();
        monthStart.setDate(1);
        const monthStartIso = monthStart.toISOString().split('T')[0];

        const [summaryData, monthTrips] = await Promise.all([
          tripService.summary({ period: 'month' }),
          tripService.getAll({ start_date: monthStartIso }),
        ]);

        setSummary(summaryData);

        const vehicleProfitMap = new Map();
        const driverProfitMap = new Map();
        const tripRows = Array.isArray(monthTrips) ? monthTrips : Array.isArray(monthTrips?.results) ? monthTrips.results : [];

        tripRows.forEach((trip) => {
          const tripProfit = asNumber(trip.profit);
          const vehicleKey = trip.vehicle_reg || 'Unknown Vehicle';
          const driverKey = trip.driver_name || 'Unassigned';

          vehicleProfitMap.set(vehicleKey, (vehicleProfitMap.get(vehicleKey) || 0) + tripProfit);
          driverProfitMap.set(driverKey, (driverProfitMap.get(driverKey) || 0) + tripProfit);
        });

        const bestVehicle = [...vehicleProfitMap.entries()].sort((a, b) => b[1] - a[1])[0];
        const bestDriver = [...driverProfitMap.entries()].sort((a, b) => b[1] - a[1])[0];

        setTopVehicle(bestVehicle ? { name: bestVehicle[0], profit: bestVehicle[1] } : null);
        setTopDriver(bestDriver ? { name: bestDriver[0], profit: bestDriver[1] } : null);
      } catch {
        setSummary(null);
        setTopVehicle(null);
        setTopDriver(null);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    const complianceItems = [
      { key: 'tlb_renewal_due_date', label: 'TLB Renewal' },
      { key: 'ntsa_inspection_due_date', label: 'NTSA Inspection' },
      { key: 'speed_governor_renewal_due_date', label: 'Speed Governor' },
      { key: 'insurance_expiry', label: 'Insurance' },
    ];

    const alerts = [];
    let overdue = 0;
    let dueSoon = 0;
    const vehicleRows = Array.isArray(vehicles) ? vehicles : Array.isArray(vehicles?.results) ? vehicles.results : [];

    console.log('dashboard payload', vehicles);

    vehicleRows.forEach((vehicle) => {
      complianceItems.forEach((item) => {
        const rawDate = vehicle[item.key];
        const state = dueState(rawDate);
        if (state === 'overdue' || state === 'due_soon') {
          alerts.push({
            vehicle: vehicle.registration_number,
            item: item.label,
            date: rawDate,
            state,
          });
          if (state === 'overdue') overdue += 1;
          if (state === 'due_soon') dueSoon += 1;
        }
      });
    });

    alerts.sort((a, b) => new Date(a.date) - new Date(b.date));
    setComplianceAlerts(alerts);
    setOverdueCount(overdue);
    setDueSoonCount(dueSoon);
  }, [vehicles]);

  if (loading) return <Loader />;

  return (
    <div>
      <div className="grid-4" style={{ marginBottom: '1.5rem' }}>
        <StatCard label="Fleet Revenue (This Month)" value={summary?.total_revenue} type="currency" accent />
        <StatCard label="Fleet Expenses (This Month)" value={summary?.total_expenses} type="currency" />
        <StatCard label="Fleet Net Profit (This Month)" value={summary?.net_profit} type="currency"
          sub={summary?.net_profit >= 0 ? 'Profitable' : 'Loss'} />
        <StatCard label="Trips (This Month)" value={summary?.total_trips} type="number" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="card">
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Quick Insights</h3></div>
          <div className="card-body">
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--gray-100)' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Compliance Overdue</span>
                <strong style={{ color: overdueCount > 0 ? 'var(--danger)' : 'var(--success)' }}>{overdueCount}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--gray-100)' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Due in 30 Days</span>
                <strong style={{ color: dueSoonCount > 0 ? 'var(--warning-text)' : 'var(--success)' }}>{dueSoonCount}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--gray-100)' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Most Profitable Vehicle</span>
                <strong style={{ textAlign: 'right' }}>{topVehicle ? `${topVehicle.name} (${formatCurrency(topVehicle.profit)})` : 'No data'}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Top Profit Driver</span>
                <strong style={{ textAlign: 'right' }}>{topDriver ? `${topDriver.name} (${formatCurrency(topDriver.profit)})` : 'No data'}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 style={{ margin: 0, fontFamily: 'var(--font-display)' }}>Compliance Warnings</h3></div>
          <div className="card-body">
            {complianceAlerts.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--success)' }}>All compliance dates are currently on track.</p>
            ) : (
              complianceAlerts.slice(0, 8).map((alert, index) => (
                <button
                  key={`${alert.vehicle}-${alert.item}-${index}`}
                  type="button"
                  onClick={() => navigate('/vehicles', { state: { editVehicleReg: alert.vehicle } })}
                  style={{
                    width: '100%',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    padding: '0.45rem 0',
                    borderBottom: '1px solid var(--gray-100)',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, textDecoration: 'underline' }}>{alert.vehicle}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{alert.item} (click to edit)</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: alert.state === 'overdue' ? 'var(--danger)' : 'var(--warning-text)' }}>
                      {alert.state === 'overdue' ? 'Overdue' : 'Due Soon'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{formatDate(alert.date)}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      <RecentTrips />
    </div>
  );
}